import React, { useEffect, useRef, useState } from "react";
import { Share2, MessageCircle, Mail, Linkedin, Instagram, MessagesSquare } from "lucide-react";
import { toast } from "sonner";

/**
 * ForwardSocialMenu — popover menu with one-click forwarding to:
 * WhatsApp, Gmail, Line, WeChat, Instagram, LinkedIn.
 * Instagram and WeChat don't expose web share URLs, so we copy the link to clipboard.
 */
export const ForwardSocialMenu = ({ url, title = "Aurora Insights", text = "", testId = "forward-menu" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const message = text ? `${text} ${shareUrl}` : shareUrl;
  const enc = encodeURIComponent;

  const copyAndNotify = async (network) => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(`Link copied — paste it into ${network}`);
    } catch {
      toast.error("Unable to copy link");
    }
  };

  const openExternal = (href) => {
    window.open(href, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const channels = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      onClick: () => openExternal(`https://wa.me/?text=${enc(message)}`),
    },
    {
      key: "gmail",
      label: "Gmail",
      icon: Mail,
      onClick: () =>
        openExternal(
          `https://mail.google.com/mail/?view=cm&fs=1&su=${enc(title)}&body=${enc(message)}`
        ),
    },
    {
      key: "line",
      label: "Line",
      icon: MessagesSquare,
      onClick: () => openExternal(`https://social-plugins.line.me/lineit/share?url=${enc(shareUrl)}`),
    },
    {
      key: "wechat",
      label: "WeChat",
      icon: MessagesSquare,
      onClick: () => {
        copyAndNotify("WeChat");
        setOpen(false);
      },
    },
    {
      key: "instagram",
      label: "Instagram",
      icon: Instagram,
      onClick: () => {
        copyAndNotify("Instagram");
        setOpen(false);
      },
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      onClick: () =>
        openExternal(`https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`),
    },
  ];

  return (
    <div className="relative inline-block" ref={ref} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="btn-ghost inline-flex items-center"
        data-testid={`${testId}-toggle`}
      >
        <Share2 size={14} className="mr-2" />
        Forward Result
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 z-50 w-64 bg-[#FAF5EE] border border-[#A88059] shadow-xl"
          data-testid={`${testId}-list`}
        >
          {channels.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                type="button"
                onClick={c.onClick}
                className="w-full flex items-center gap-3 px-5 py-3 text-left text-[#190F08] font-body text-base hover:bg-[#A88059]/10 transition-colors border-b border-[#A88059]/20 last:border-b-0"
                data-testid={`${testId}-${c.key}`}
              >
                <Icon size={16} className="text-[#A88059]" />
                {c.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
